/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.13.0
 * @since 1.13.0
 */

import { ArrayElement } from '../../../../types/globalTypes';
import { EDcActiveRuleType, IDcRuleActive, IDcRuleActiveItem, IDcRuleItem } from './types';


export interface IDcRuleListResponse {
    list: Array<{
        dateChange?: string[];
        dateEnd?: string;
        dateStart?: string;
        id: number;
        name: string;
        status: number;
    }>;
}

export interface IDcActiveRuleItemResponse {
    children?: IDcActiveRuleItemResponse[];
    limit?: {
        breakLimit?: number;
        measure?: string;
        warnLimit?: number;
    };
    name: string;
    type?: EDcActiveRuleType;
}

export interface IDcActiveRuleResponse {
    id: number;

    rules: IDcActiveRuleItemResponse[];

    // Оповещение абонентов
    notification?: {
        notifyOverBreak: boolean;
        notifyOverWarning: boolean;
        notifySetOrChangeLimits: boolean;
        notifyStartUseLimits: boolean;
    };

    // Расписание действия
    scheduleData: Array<{
        day: number; // Можно начинать с 0?
        time: {
            start: string; // HH:MM:SS
            end: string; // HH:MM:SS
        };
    }>;
}


export const dcRulesListMapping = (response: IDcRuleListResponse): IDcRuleItem[] => {
    return response.list
            // mapping
            ?.map(({ status, ...rule }: ArrayElement<IDcRuleListResponse['list']>) =>
                ({ ...rule, isActive: status === 1 }))
            // сортировка по статусу и времени подключения
            .sort((a, b) => {
                // если статус одинаковый, то сортируем по дате подключения
                if (a.isActive === b.isActive) {
                    const dateA = a.dateStart || a.dateEnd;
                    const dateB = b.dateStart || b.dateEnd;
                    if (!dateA) return -1;
                    if (!dateB) return 1;
                    return new Date(dateA) > new Date(dateB) ? 1 : -1;
                }
                return a.isActive ? -1 : 1;
            })
        || [];
};

const mapActiveRule = (item: IDcActiveRuleItemResponse): IDcRuleActiveItem => ({
    child: item.children?.map(mapActiveRule) || [],
    limit: {
        break: item.limit?.breakLimit,
        measure: item.limit?.measure,
        warn: item.limit?.warnLimit,
    },
    name: item.name,
    type: item.type,
});

export const dcRulesActiveMapping = (
    { notification, rules, scheduleData, ...response }: IDcActiveRuleResponse,
): IDcRuleActive => {
    // С бека приходят только те дни недели, у которых указано время, поэтому генерируем полное пустое расписание
    const scheduleList: IDcRuleActive['scheduleList'] = Array.from(Array(7).keys()).map((day: number) => ({ day }));
    // Для заданных дней переопределяем время
    scheduleData.forEach(schedule => {
        // Нумерация дней на беке начинается с 3 :)
        if (scheduleList[schedule.day - 3]) {
            scheduleList[schedule.day - 3].time = schedule.time;
        }
    });
    return ({
        ...response,
        notification: {
            notifyOverBreak: notification?.notifyOverBreak || false,
            notifyOverWarning: notification?.notifyOverWarning || false,
            notifySetOrChangeLimits: notification?.notifySetOrChangeLimits || false,
            notifyStartUseLimits: notification?.notifyStartUseLimits || false,
        },
        scheduleList,
        rules: rules.map(mapActiveRule),
    });
};

